import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import {
    Buildings,
    UsersThree,
    Briefcase,
    UserCircle,
    Plus,
    Crown,
    X,
    ShieldCheck,
    ChartLineUp,
    SealCheck,
    ArrowRight,
} from "@phosphor-icons/react";

const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString("en-IN", {
            day: "numeric", month: "short", year: "numeric",
        });
    } catch { return "—"; }
};

const PLAN_TONE = {
    free: "bg-[#F5F5F5] text-[#525252]",
    pro: "bg-cobalt/10 text-cobalt",
    enterprise: "bg-ink text-splash",
};

export default function SuperAdminConsole() {
    const [workspaces, setWorkspaces] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showCreate, setShowCreate] = useState(false);
    const [form, setForm] = useState({ name: "", slug: "", plan: "free" });
    const [creating, setCreating] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get("/workspaces");
            setWorkspaces(r.data.items || []);
        } catch (err) {
            toast.error(err?.response?.status === 403
                ? "Superadmin only — you don't have platform-level access."
                : "Failed to load workspaces");
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const create = async (e) => {
        e?.preventDefault();
        const slug = form.slug.trim().toLowerCase();
        if (!/^[a-z0-9][a-z0-9-]{0,30}[a-z0-9]$/.test(slug)) {
            toast.error("Slug must be lowercase letters, digits, and hyphens.");
            return;
        }
        setCreating(true);
        try {
            await api.post("/workspaces", { name: form.name.trim(), slug, plan: form.plan });
            toast.success(`Workspace "${form.name}" provisioned.`);
            setForm({ name: "", slug: "", plan: "free" });
            setShowCreate(false);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't create workspace");
        } finally { setCreating(false); }
    };

    const totals = workspaces.reduce((acc, w) => ({
        users: acc.users + (w.counts?.users || 0),
        candidates: acc.candidates + (w.counts?.candidates || 0),
        jobs: acc.jobs + (w.counts?.jobs || 0),
        clients: acc.clients + (w.counts?.clients || 0),
    }), { users: 0, candidates: 0, jobs: 0, clients: 0 });

    return (
        <div className="max-w-7xl mx-auto px-3 sm:px-6 py-5 sm:py-8 overflow-x-hidden" data-testid="superadmin-console">
            <div className="flex items-start justify-between flex-wrap gap-3 sm:gap-4 mb-5 sm:mb-6">
                <div className="min-w-0">
                    <p className="text-[10px] font-black uppercase tracking-[0.2em] text-splash mb-1 inline-flex items-center gap-1.5">
                        <ShieldCheck size={12} weight="fill" /> Platform-level access
                    </p>
                    <h1 className="font-heading text-2xl sm:text-4xl font-black tracking-tight">
                        Workspaces
                    </h1>
                    <p className="text-xs sm:text-sm text-[#525252] mt-1">
                        Provision agencies · brand tenants · impersonate users · manage plans.
                    </p>
                </div>
                <button
                    onClick={() => setShowCreate(true)}
                    data-testid="superadmin-create-btn"
                    className="btn-gradient px-4 sm:px-5 py-2 sm:py-2.5 text-xs font-black uppercase tracking-wider inline-flex items-center gap-2 whitespace-nowrap"
                >
                    <Plus size={14} weight="bold" /> <span className="hidden sm:inline">New workspace</span><span className="sm:hidden">New</span>
                </button>
            </div>

            {/* Platform totals */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8" data-testid="superadmin-totals">
                {[
                    { label: "Workspaces", value: workspaces.length, icon: Buildings },
                    { label: "Users", value: totals.users, icon: UsersThree },
                    { label: "Candidates", value: totals.candidates, icon: UserCircle },
                    { label: "Jobs · Clients", value: `${totals.jobs} · ${totals.clients}`, icon: Briefcase },
                ].map(({ label, value, icon: Icon }) => (
                    <div key={label} className="bg-white border border-[#E5E5E5] p-5">
                        <p className="text-[10px] font-black uppercase tracking-wider text-[#A3A3A3] mb-2 flex items-center gap-1.5">
                            <Icon size={12} weight="bold" /> {label}
                        </p>
                        <p className="font-heading text-3xl font-black tracking-tighter">{value}</p>
                    </div>
                ))}
            </div>

            {/* Platform tools quick-links */}
            <div className="flex flex-wrap gap-2 mb-8" data-testid="superadmin-tool-links">
                <Link
                    to="/superadmin/api-explorer"
                    data-testid="superadmin-link-api-explorer"
                    className="inline-flex items-center gap-1.5 px-3 py-2 border border-[#E5E5E5] bg-white hover:bg-[#FAFAFA] text-[11px] font-black uppercase tracking-wider text-ink"
                >
                    <ChartLineUp size={12} weight="bold" /> API Explorer
                </Link>
                <Link
                    to="/superadmin/growth"
                    data-testid="superadmin-link-growth"
                    className="inline-flex items-center gap-1.5 px-3 py-2 border border-[#E5E5E5] bg-white hover:bg-[#FAFAFA] text-[11px] font-black uppercase tracking-wider text-ink"
                >
                    <ChartLineUp size={12} weight="bold" /> Growth
                </Link>
                <Link
                    to="/superadmin/audit"
                    data-testid="superadmin-link-audit"
                    className="inline-flex items-center gap-1.5 px-3 py-2 border border-[#E5E5E5] bg-white hover:bg-[#FAFAFA] text-[11px] font-black uppercase tracking-wider text-ink"
                >
                    <ShieldCheck size={12} weight="bold" /> Audit log
                </Link>
                <Link
                    to="/superadmin/verification"
                    data-testid="superadmin-link-verification"
                    className="inline-flex items-center gap-1.5 px-3 py-2 border border-cobalt/30 bg-cobalt/5 hover:bg-cobalt/10 text-[11px] font-black uppercase tracking-wider text-cobalt"
                >
                    <SealCheck size={12} weight="fill" /> Verification analytics
                </Link>
                <Link
                    to="/superadmin/schedulers"
                    data-testid="superadmin-link-schedulers"
                    className="inline-flex items-center gap-1.5 px-3 py-2 border border-[#E5E5E5] bg-white hover:bg-[#FAFAFA] text-[11px] font-black uppercase tracking-wider text-ink"
                >
                    <ShieldCheck size={12} weight="bold" /> Schedulers
                </Link>
                <Link
                    to="/superadmin/partner-waitlist"
                    data-testid="superadmin-link-partner-waitlist"
                    className="inline-flex items-center gap-1.5 px-3 py-2 border border-emerald-500/40 bg-emerald-50 hover:bg-emerald-100 text-[11px] font-black uppercase tracking-wider text-emerald-800"
                >
                    <ShieldCheck size={12} weight="bold" /> Partner waitlist
                </Link>
            </div>

            {/* Workspaces table */}
            {loading && <p className="text-sm text-[#A3A3A3]">Loading…</p>}
            {!loading && workspaces.length === 0 && (
                <div className="bg-white border border-[#E5E5E5] p-8 text-center">
                    <Buildings size={28} weight="regular" className="text-[#A3A3A3] mx-auto mb-2" />
                    <p className="text-sm text-[#525252]">No workspaces yet — provision one to get started.</p>
                </div>
            )}
            {workspaces.length > 0 && (
                <>
                {/* Mobile card list — V2.0.67 sweep to kill horizontal scroll
                    on ops pages. Table stays for sm+. */}
                <div className="sm:hidden space-y-2" data-testid="superadmin-ws-cards">
                    {workspaces.map((w) => (
                        <Link key={w.id} to={`/superadmin/workspaces/${w.id}`}
                              data-testid={`superadmin-ws-card-${w.slug}`}
                              className="block bg-white border border-[#E5E5E5] p-3 hover:bg-[#FAFAFA]">
                            <div className="flex items-start justify-between gap-2 mb-2">
                                <div className="font-heading font-bold text-ink flex items-center gap-1.5 min-w-0 truncate">
                                    {w.slug === "default" && <Crown size={12} weight="fill" className="text-splash shrink-0" />}
                                    <span className="truncate">{w.name}</span>
                                </div>
                                <span className={`text-[10px] font-black uppercase tracking-wider px-2 py-0.5 shrink-0 ${PLAN_TONE[w.plan] || PLAN_TONE.free}`}>
                                    {w.plan}
                                </span>
                            </div>
                            <div className="text-[11px] font-mono text-[#A3A3A3] mb-2">{w.slug}</div>
                            <div className="grid grid-cols-4 gap-2 text-center border-t border-[#F5F5F5] pt-2">
                                {[["Users", w.counts?.users], ["Candidates", w.counts?.candidates], ["Jobs", w.counts?.jobs], ["Clients", w.counts?.clients]].map(([label, v]) => (
                                    <div key={label}>
                                        <div className="text-[9px] font-black uppercase tracking-wider text-[#A3A3A3]">{label}</div>
                                        <div className="font-heading font-black text-lg">{v || 0}</div>
                                    </div>
                                ))}
                            </div>
                        </Link>
                    ))}
                </div>
                <div className="hidden sm:block bg-white border border-[#E5E5E5] overflow-x-auto">
                    <table className="w-full text-sm min-w-[860px]">
                        <thead className="bg-[#FAFAFA] border-b border-[#E5E5E5]">
                            <tr className="text-left">
                                {["Name", "Slug", "Plan", "Created", "Users", "Candidates", "Jobs", "Clients", ""].map((h) => (
                                    <th key={h} className="px-4 py-3 text-[10px] font-black uppercase tracking-wider text-[#525252]">{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {workspaces.map((w) => (
                                <tr key={w.id} data-testid={`superadmin-ws-${w.slug}`} className="border-b border-[#F5F5F5] hover:bg-[#FAFAFA]">
                                    <td className="px-4 py-3">
                                        <Link to={`/superadmin/workspaces/${w.id}`}
                                              data-testid={`superadmin-ws-link-${w.slug}`}
                                              className="font-heading font-bold text-ink flex items-center gap-2 hover:text-cobalt">
                                            {w.slug === "default" && <Crown size={12} weight="fill" className="text-splash" />}
                                            {w.settings?.logo_url && (
                                                <img src={w.settings.logo_url} alt="" className="h-4 w-auto"
                                                     onError={(e) => { e.currentTarget.style.display = "none"; }} />
                                            )}
                                            {w.name}
                                        </Link>
                                    </td>
                                    <td className="px-4 py-3 text-xs font-mono text-[#525252]">{w.slug}</td>
                                    <td className="px-4 py-3">
                                        <span className={`text-[10px] font-black uppercase tracking-wider px-2 py-0.5 ${PLAN_TONE[w.plan] || PLAN_TONE.free}`}>
                                            {w.plan}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-xs text-[#525252]">{fmtDate(w.created_at)}</td>
                                    <td className="px-4 py-3 font-heading font-black">{w.counts?.users || 0}</td>
                                    <td className="px-4 py-3 font-heading font-black">{w.counts?.candidates || 0}</td>
                                    <td className="px-4 py-3 font-heading font-black">{w.counts?.jobs || 0}</td>
                                    <td className="px-4 py-3 font-heading font-black">{w.counts?.clients || 0}</td>
                                    <td className="px-4 py-3">
                                        <Link to={`/superadmin/workspaces/${w.id}`}
                                              data-testid={`superadmin-manage-${w.slug}`}
                                              className="text-cobalt hover:text-ink inline-flex items-center gap-1 text-xs font-black uppercase tracking-wider">
                                            Manage <ArrowRight size={12} weight="bold" />
                                        </Link>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                </>
            )}

            {/* Create workspace modal */}
            {showCreate && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
                     onClick={() => setShowCreate(false)}>
                    <div className="bg-white w-full max-w-lg p-6 sm:p-8 relative"
                         onClick={(e) => e.stopPropagation()}>
                        <button
                            onClick={() => setShowCreate(false)}
                            className="absolute top-4 right-4 text-[#A3A3A3] hover:text-ink"
                            data-testid="superadmin-create-close"
                        >
                            <X size={16} weight="bold" />
                        </button>
                        <p className="text-[10px] font-black uppercase tracking-[0.2em] text-cobalt mb-1">
                            Provision workspace
                        </p>
                        <h2 className="font-heading text-2xl font-black tracking-tight mb-1">
                            New agency
                        </h2>
                        <p className="text-sm text-[#525252] mb-6">
                            Creates an isolated tenant. Invite their admin from the workspace detail page after creation.
                        </p>
                        <form onSubmit={create} data-testid="superadmin-create-form" className="space-y-4">
                            <div>
                                <label className="text-[10px] font-black uppercase tracking-wider text-[#525252] block mb-1.5">
                                    Agency name
                                </label>
                                <input
                                    value={form.name}
                                    onChange={(e) => {
                                        const n = e.target.value;
                                        setForm({
                                            ...form, name: n,
                                            slug: form.slug || n.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "").slice(0, 32),
                                        });
                                    }}
                                    data-testid="superadmin-form-name"
                                    placeholder="Acme Recruiting"
                                    required
                                    className="w-full border border-[#E5E5E5] px-3 py-2.5 text-sm focus:outline-none focus:border-ink"
                                />
                            </div>
                            <div>
                                <label className="text-[10px] font-black uppercase tracking-wider text-[#525252] block mb-1.5">
                                    Slug (URL identifier)
                                </label>
                                <input
                                    value={form.slug}
                                    onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase() })}
                                    data-testid="superadmin-form-slug"
                                    placeholder="acme"
                                    pattern="^[a-z0-9][a-z0-9\-]{0,30}[a-z0-9]$"
                                    required
                                    className="w-full border border-[#E5E5E5] px-3 py-2.5 text-sm focus:outline-none focus:border-ink font-mono"
                                />
                                <p className="text-[10px] text-[#A3A3A3] mt-1">
                                    lowercase letters, digits, hyphens. 2–32 chars.
                                </p>
                            </div>
                            <div>
                                <label className="text-[10px] font-black uppercase tracking-wider text-[#525252] block mb-2">
                                    Plan
                                </label>
                                <div className="grid grid-cols-3 gap-2">
                                    {["free", "pro", "enterprise"].map((p) => (
                                        <button
                                            type="button"
                                            key={p}
                                            onClick={() => setForm({ ...form, plan: p })}
                                            data-testid={`superadmin-form-plan-${p}`}
                                            className={`text-xs font-black uppercase tracking-wider px-3 py-2.5 border transition-colors ${
                                                form.plan === p ? "bg-ink text-white border-ink" : "border-[#E5E5E5] hover:border-ink"
                                            }`}
                                        >
                                            {p}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div className="flex justify-end gap-2 pt-2">
                                <button type="button" onClick={() => setShowCreate(false)}
                                        className="px-4 py-2.5 text-xs font-black uppercase tracking-wider text-[#525252] hover:text-ink">
                                    Cancel
                                </button>
                                <button type="submit" disabled={creating} data-testid="superadmin-form-submit"
                                        className="btn-gradient px-5 py-2.5 text-xs font-black uppercase tracking-wider inline-flex items-center gap-2 disabled:opacity-60">
                                    {creating ? "Creating…" : (<><ChartLineUp size={12} weight="bold" /> Provision</>)}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
